/**
 * @file SidebarMenuMain.tsx
 * @module _admin/layout/components/sidebar/sidebar-menu/SidebarMenuMain
 * @description Renders the SidebarMenuMain navigation component used to compose the admin sidebar menu.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { SidebarMenuItem } from "./SidebarMenuItem";
import { SidebarMenuItemWithSub } from "./SidebarMenuItemWithSub";
import MasterSVG from "../../../../../_admin/assets/media/dashboard/master.svg";
import PlatformSVG from "../../../../../_admin/assets/media/dashboard/platform.svg";
import AdvertisementSVG from "../../../../../_admin/assets/media/dashboard/advertisement.svg";
import ContactSVG from "../../../../../_admin/assets/media/dashboard/contact.svg";
import ProductSVG from "../../../../../_admin/assets/media/dashboard/inventory.svg";
import DashboardIcon from "../../../../../_admin/assets/media/dashboard/dashboard.svg";
import CustomersIcon from "../../../../../_admin/assets/media/dashboard/customers.svg";
import UsersIcon from "../../../../../_admin/assets/media/dashboard/users.svg";
import OrdersDeliverySVG from "../../../../../_admin/assets/media/dashboard/orders-delivery.svg";
import PendingOrder from "../../../../../_admin/assets/media/dashboard/seller.svg";
import React, { useEffect, useState } from "react";
import APICallService from "../../../../../api/apiCallService";
import { SIDEBAR } from "../../../../../api/apiEndPoints";
import { useAuth } from "../../../../../app/modules/auth";
import CustomerSVG from "../../../../../_admin/assets/media/dashboard/customers.svg";
import NotificationIcon from "../../../../assets/media/svg_uTrade/bell-icon.svg";
import SalesRepresentativeSVG from "../../../../../_admin/assets/media/dashboard/sales-representative.svg";
import CustomerAssignmentSVG from "../../../../../_admin/assets/media/dashboard/customer_assignment.svg";
import TargetManagementSVG from "../../../../../_admin/assets/media/dashboard/target_management.svg";
import VehicleSVG from "../../../../../_admin/assets/media/dashboard/vehicle.svg";
import DriverSVG from "../../../../../_admin/assets/media/dashboard/driver.svg";
import DistrictSVG from "../../../../../_admin/assets/media/dashboard/district.svg";
import CatalogueSVG from "../../../../../_admin/assets/media/dashboard/catalogue.svg";

import CustomerCategorySVG from "../../../../../_admin/assets/media/dashboard/customer-category.svg";
import CustomerLedgerSVG from "../../../../../_admin/assets/media/dashboard/customer-ledger.svg";
import DriverLedgerSVG from "../../../../../_admin/assets/media/dashboard/driver-ledger.svg";
import RefundToCustomerSVG from "../../../../../_admin/assets/media/dashboard/refund-to-customer.svg";
import ReceivableReportSVG from "../../../../../_admin/assets/media/dashboard/receivable-report.svg";
import PreOrderCustomerSVG from "../../../../../_admin/assets/media/dashboard/pre-order-customer.svg";
import {
  Admin,
  AssistAdmin,
  FinanceManager,
  FulfillmentManager,
  LoadingBayManager,
  Manager,
  PermissionModule,
  ServiceMember,
  ActivationManager,
  ActivationRep,
} from "../../../../../utils/constants";
import InventorySVG from "../../../../../_admin/assets/media/dashboard/_inventory.svg";
import CategoryVG from "../../../../../_admin/assets/media/dashboard/category.svg";
import BrandSVG from "../../../../../_admin/assets/media/dashboard/brand.svg";
import DetailsSVG from "../../../../../_admin/assets/media/dashboard/details.svg";
import ProductVariantsSVG from "../../../../../_admin/assets/media/dashboard/product-variants.svg";
import SuppliersSVG from "../../../../../_admin/assets/media/dashboard/suppliers.svg";
import PaymentSVG from "../../../../../_admin/assets/media/dashboard/payment.svg";
import SettingsSVG from "../../../../../_admin/assets/media/dashboard/settings.svg";
import PurchaseOrderSVG from "../../../../../_admin/assets/media/dashboard/purchase-order.svg";
import GoodsReceivedNoteSVG from "../../../../../_admin/assets/media/dashboard/goods-received-note.svg";
import {
  canPerformInwardAction,
  InwardAction,
} from "../../../../../app/modules/inward/utils/inward-access";
import AssignProductsSVG from "../../../../../_admin/assets/media/dashboard/assign-products.svg";
import SupplierLedgerSVG from "../../../../../_admin/assets/media/dashboard/supplier-ledger.svg";
import EscalationsSVG from "../../../../../_admin/assets/media/dashboard/escalations.svg";
import SalesSVG from "../../../../../_admin/assets/media/dashboard/sales.svg";
import WarehouseSVG from "../../../../../_admin/assets/media/dashboard/warehouse.svg";
import Method from "../../../../../utils/methods";
import InterTransferRequestUmartSVG from "../../../../../_admin/assets/media/dashboard/inter-transfer-request-umart.svg";
import InterTransferRequestSVG from "../../../../../_admin/assets/media/dashboard/inter-transfer-request.svg";
import GoodsInWarehouseSVG from "../../../../../_admin/assets/media/dashboard/goods-in-warehouse.svg";
import OutOfStockListSVG from "../../../../../_admin/assets/media/dashboard/out-of-stock-list.svg";

// The new tab-based Procurement and Master pages are hidden until they are fixed,
// so the old menus show instead. Set a flag to true to swap that menu back to the new page.
const showNewProcurementMenu = false;
const showNewMasterMenu = false;

const SidebarMenuMain = () => {
  const { currentUser } = useAuth();
  const [count, setCount] = useState({
    pendingKYC: 0,
    totalSellers: 0,
    pendingProducts: 0,
  });

  useEffect(() => {
    (async () => {
      if (currentUser?.userType === AssistAdmin) {
      } else {
        await fetchCount();
      }
    })();
  }, []);
  const fetchCount = async () => {
    let apiService = new APICallService(SIDEBAR);
    let response = await apiService.callAPI();
    if (response) {
      setCount(response);
    }
  };
  const renderAssistAdminItems = () => {
    const priceCatalogItem =
      Method.hasModulePermission(PermissionModule.PriceCatalog, currentUser) ? (
        <SidebarMenuItem
          key="price-catalog"
          to="/price-catalog"
          icon={CatalogueSVG}
          title="Price Catalog"
          fontIcon="bi-app-indicator"
          className="release-highlighter--sidebar-menu-price-catalog"
        />
      ) : null;

    if (
      currentUser?.role.some(
        (obj: any) => obj.type === Manager || obj.type === ActivationManager,
      )
    ) {
      return (
        <>
          <SidebarMenuItem
            key="manager-dashboard"
            to="/sales-management/manager/dashboard"
            icon={DashboardIcon}
            title={"Dashboard"}
            fontIcon="bi-app-indicator"
          />
          <SidebarMenuItem
            key="manager-sales-representative"
            to="/sales-management/manager/sales-representative"
            icon={SalesRepresentativeSVG}
            title={
              currentUser?.role.some((obj: any) => obj.type === Manager)
                ? "Sales Representative"
                : "Activation Representative"
            }
            fontIcon="bi-app-indicator"
          />
          <SidebarMenuItem
            key="manager-customer-assignment"
            to="/sales-management/manager/customer-assignment"
            icon={CustomerAssignmentSVG}
            title={"Customer Assignment"}
            fontIcon="bi-app-indicator"
          />
          <SidebarMenuItem
            key="manager-target-management"
            to="/sales-management/manager/target-management"
            title="Target Management"
            icon={TargetManagementSVG}
            fontIcon="bi-person"
          />
          <SidebarMenuItem
            key="manager-orders"
            to="/sales-management/manager/orders"
            title="Orders"
            icon={OrdersDeliverySVG}
            fontIcon="bi-person"
          />
          {priceCatalogItem}
        </>
      );
    } else if (
      currentUser?.role.some(
        (obj: any) => obj.type === ServiceMember || obj.type === ActivationRep,
      )
    ) {
      return (
        <>
          <SidebarMenuItem
            key="representative-dashboard"
            to="/sales-management/representative/dashboard"
            icon={DashboardIcon}
            title={"Dashboard"}
            fontIcon="bi-app-indicator"
          />
          <SidebarMenuItem
            key="representative-customer-management"
            to="/sales-management/representative/customer-management"
            icon={CustomersIcon}
            title={"Customer Management"}
            fontIcon="bi-app-indicator"
          />
          <SidebarMenuItem
            key="representative-orders"
            to="/sales-management/representative/orders"
            title="Orders"
            icon={OrdersDeliverySVG}
            fontIcon="bi-person"
          />
          <SidebarMenuItem
            key="representative-target-tracking"
            to="/sales-management/representative/target-tracking"
            title="Target Tracking"
            icon={TargetManagementSVG}
            fontIcon="bi-person"
          />
          {priceCatalogItem}
        </>
      );
    } else if (
      currentUser?.role.some((obj: any) => obj.type === FinanceManager)
    ) {
      return (
        <>
          <SidebarMenuItem
            key="finance-manager-customers"
            to="/customers/view-customers"
            icon={CustomersIcon}
            title={"Customers"}
            fontIcon="bi-app-indicator"
          />
          <SidebarMenuItemWithSub
            key="finance-manager-reports"
            to="/finance-reports/"
            title="Finance Reports"
            icon={MasterSVG}
            fontIcon="bi-person"
          >
            <SidebarMenuItem
              key="finance-manager-sales-report"
              to="/finance-reports/finance-sales-report"
              title="Sales Report"
            />
            <SidebarMenuItem
              key="finance-manager-delivery-report"
              to="/finance-reports/finance-order-report"
              title="Order Report"
            />
            <SidebarMenuItem
              key="finance-manager-outward-report"
              to="/finance-reports/finance-outward-report"
              title="Outward Report"
            />
            <SidebarMenuItem
              key="finance-manager-return-report"
              to="/finance-reports/finance-return-report"
              title="Return Report"
            />
            <SidebarMenuItem
              key="finance-manager-delivery-report-actual"
              to="/finance-reports/finance-delivery-report"
              title="Delivery Report"
            />
            <SidebarMenuItem
              key="finance-manager-v9-outward-report"
              to="/finance-reports/v9-outward-report"
              title="Dispatch Report"
            />

            <SidebarMenuItem
              key="finance-manager-v10-outward-report"
              to="/finance-reports/v10-outward-report"
              title="Dispatch Report (Only Return Considered)"
            />

            <SidebarMenuItem
              key="finance-manager-v8-outward-report"
              to="/finance-reports/v8-outward-report"
              title="Dispatch Report With Return Combined"
            />

            <SidebarMenuItem
              key="finance-manager-return-product-report"
              to="finance-reports/return-product-report"
              title="Return Product Reports"
            />
            <SidebarMenuItem
              key="finance-manager-route-products-report"
              to="/finance-reports/route-products-report"
              title="Delivery Plan Report"
            />
            <SidebarMenuItem
              key="finance-manager-driver-reconciliation-report"
              to="/finance-reports/driver-reconciliation-report"
              title="Driver Reconciliation Report"
            />
          </SidebarMenuItemWithSub>
          <SidebarMenuItem
            key="finance-manager-orders"
            to="/orders"
            activeMatch="/orders-delivery"
            title="Orders & Delivery"
            icon={OrdersDeliverySVG}
            fontIcon="bi-person"
          />
          <SidebarMenuItem
            key="finance-manager-outstanding-report"
            to="/outstanding-report"
            title="Outstanding Report"
            icon={OrdersDeliverySVG}
            fontIcon="bi-person"
          />
          <SidebarMenuItem
            key="finance-manager-all-products"
            to="/all-products"
            title="All products"
            icon={ProductSVG}
            fontIcon="bi-person"
            hasCount={count.pendingProducts > 0}
            counter={count.pendingProducts}
          />
          <SidebarMenuItem
            key="finance-manager-price-update-request"
            to="/price-update-request"
            title="Price Update Request"
            icon={ProductSVG}
            fontIcon="bi-person"
          />
          <SidebarMenuItemWithSub
            key="finance-manager-inventory"
            to="/inventory/"
            title="Inventory"
            icon={InventorySVG}
            fontIcon="bi-person"
          >
            <SidebarMenuItem
              key="finance-manager-goods-in-warehouse"
              to="/inventory/goods-in-warehouse"
              title="Goods in warehouse"
              icon={GoodsInWarehouseSVG}
              fontIcon="bi-person"
            />
            <SidebarMenuItem
              key="finance-manager-low-stock-list"
              to="/inventory/low-stock-list"
              title="Low stock list"
            />
          </SidebarMenuItemWithSub>
          <SidebarMenuItem
            key="finance-manager-advertising"
            to="/advertisement/view-advertisement"
            title="Advertising"
            icon={AdvertisementSVG}
            fontIcon="bi-person"
          />
          <SidebarMenuItem
            key="finance-manager-platform-fees"
            to="/platform-fees/fees"
            icon={PlatformSVG}
            title="Platform fees"
            fontIcon="bi-app-indicator"
          />
          {priceCatalogItem}
        </>
      );
    } else if (
      currentUser?.role.some((obj: any) => obj.type === LoadingBayManager)
    ) {
      return (
        <>
          <SidebarMenuItem
            key="loading-bay-orders"
            to="/orders"
            activeMatch="/orders-delivery"
            title="Orders & delivery"
            icon={OrdersDeliverySVG}
            fontIcon="bi-person"
          />
          <SidebarMenuItem
            key="loading-bay-outstanding-report"
            to="/outstanding-report"
            title="Outstanding Report"
            icon={OrdersDeliverySVG}
            fontIcon="bi-person"
          />
          <SidebarMenuItem
            key="pendingdeliveryorders"
            to="/pending-delivery-orders"
            title="Pending Delivery Orders"
            icon={PendingOrder}
            fontIcon="bi-person"
          />
          <SidebarMenuItemWithSub
            key="loading-bay-inventory"
            to="/inventory/"
            title="Inventory"
            icon={InventorySVG}
            fontIcon="bi-person"
          >
            <SidebarMenuItem
              key="loading-bay-goods-in-warehouse"
              to="/inventory/goods-in-warehouse"
              title="Goods in warehouse"
              icon={GoodsInWarehouseSVG}
              fontIcon="bi-person"
            />
            <SidebarMenuItem
              key="loading-bay-low-stock-list"
              to="/inventory/low-stock-list"
              title="Low stock list"
            />
          </SidebarMenuItemWithSub>
          {priceCatalogItem}
        </>
      );
    } else if (
      currentUser?.role.some((obj: any) => obj.type === FulfillmentManager)
    ) {
      return (
        <>
          <SidebarMenuItem
            key="fulfillment-customers"
            to="/customers/view-customers"
            icon={CustomersIcon}
            title={"Customers"}
            fontIcon="bi-app-indicator"
          />
          <SidebarMenuItem
            key="fulfillment-orders"
            to="/orders"
            activeMatch="/orders-delivery"
            title="Orders & delivery"
            icon={OrdersDeliverySVG}
            fontIcon="bi-person"
          />
          <SidebarMenuItem
            key="fulfillment-outstanding-report"
            to="/outstanding-report"
            title="Outstanding Report"
            icon={OrdersDeliverySVG}
            fontIcon="bi-person"
          />
          <SidebarMenuItem
            key="pendingdeliveryorders"
            to="/pending-delivery-orders"
            title="Pending Delivery Orders"
            icon={PendingOrder}
            fontIcon="bi-person"
          />
          <SidebarMenuItemWithSub
            key="fulfillment-master"
            to="/master-old/drivers"
            title="Master"
            icon={MasterSVG}
            fontIcon="bi-person"
          >
            <SidebarMenuItem
              key="fulfillment-drivers"
              to="/master-old/drivers"
              title="Drivers"
            />
            <SidebarMenuItem
              key="fulfillment-vehicles"
              to="/master-old/vehicles"
              title="Vehicles"
            />
          </SidebarMenuItemWithSub>
          <SidebarMenuItemWithSub
            key="fulfillment-inventory"
            to="/inventory/"
            title="Inventory"
            icon={InventorySVG}
            fontIcon="bi-person"
          >
            <SidebarMenuItem
              key="fulfillment-goods-in-warehouse"
              to="/inventory/goods-in-warehouse"
              title="Goods in warehouse"
              icon={GoodsInWarehouseSVG}
              fontIcon="bi-person"
            />
            <SidebarMenuItem
              key="fulfillment-low-stock-list"
              to="/inventory/low-stock-list"
              title="Low stock list"
            />
          </SidebarMenuItemWithSub>
          {priceCatalogItem}
        </>
      );
    } else {
      return renderMenuItems();
    }
  };
  const renderMenuItems = () => {
    const menuItems = [];
    // Dashboard
    if (Method.hasModulePermission(PermissionModule.Dashboard, currentUser)) {
      menuItems.push(
        <SidebarMenuItem
          key="dashboard"
          to="/dashboard"
          icon={DashboardIcon}
          title={"Dashboard"}
          fontIcon="bi-app-indicator"
        />,
      );
    }

    const salesManagementAdminItems = [];
    if (
      Method.hasModulePermission(PermissionModule.SalesDashboard, currentUser)
    ) {
      salesManagementAdminItems.push(
        <SidebarMenuItem
          key="sales-dashboard"
          to="/sales-management/admin/dashboard"
          title="Dashboard"
        />,
      );
    }

    if (
      Method.hasModulePermission(PermissionModule.TargetManagement, currentUser)
    ) {
      salesManagementAdminItems.push(
        <SidebarMenuItem
          key="sales-user-management"
          to="/sales-management/admin/user-management"
          title="User Management"
        />,
      );
    }
    if (
      Method.hasModulePermission(PermissionModule.TargetManagement, currentUser)
    ) {
      salesManagementAdminItems.push(
        <SidebarMenuItem
          key="sales-target-management"
          to="/sales-management/admin/target-management"
          title="Target Management"
        />,
      );
    }

    if (
      Method.hasModulePermission(PermissionModule.SalesRepOrders, currentUser)
    ) {
      salesManagementAdminItems.push(
        <SidebarMenuItem
          key="sales-orders"
          to="/sales-management/admin/orders"
          title="Orders"
        />,
      );
    }

    if (salesManagementAdminItems.length > 0) {
      menuItems.push(
        <SidebarMenuItemWithSub
          key="sales-management"
          to="#"
          icon={SalesSVG}
          title="Sales"
          fontIcon="bi-app-indicator"
        >
          {salesManagementAdminItems.map((item: any, index: number) => {
            return <React.Fragment key={index}>{item}</React.Fragment>;
          })}
        </SidebarMenuItemWithSub>,
      );
    }

    const uProcureItems = [];

    if (
      Method.hasModulePermission(
        PermissionModule.UProcureDashboard,
        currentUser,
      ) ||
      currentUser?.roleAndPermission.isWarehouseManagerRole ||
      currentUser?.roleAndPermission?.roleType === "R0001" ||
      currentUser?.roleAndPermission?.roleType === "R0003"
    ) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-dashboard"
          to="/UProcure/master/dashboard"
          title="Dashboard"
        />,
      );
    }
    if (Method.hasModulePermission(PermissionModule.Categories, currentUser)) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-categories"
          to="/UProcure/master/categories"
          title="Category"
          icon={CategoryVG}
          fontIcon="bi-app-indicator"
          activeMatch="categories"
        />,
      );
    }
    if (Method.hasModulePermission(PermissionModule.Brand, currentUser)) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-brands"
          to="/UProcure/master/brands"
          title="Brands"
          icon={BrandSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }
    if (
      Method.hasModulePermission(PermissionModule.TechnicalDetails, currentUser)
    ) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-technical-details"
          to="/UProcure/master/technical-details"
          title="Technical Details"
          icon={DetailsSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }

    if (
      Method.hasModulePermission(PermissionModule.ProductVariants, currentUser)
    ) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-product-variants"
          to="/UProcure/master/product-variants"
          title="Product Variants"
          icon={ProductVariantsSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }

    if (Method.hasModulePermission(PermissionModule.AllVendors, currentUser)) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-suppliers"
          to="/UProcure/vendors/list-vendors"
          title="Suppliers"
          icon={SuppliersSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }

    if (
      Method.hasModulePermission(
        PermissionModule.AssignProductToVendors,
        currentUser,
      )
    ) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-assign-products"
          to="/UProcure/assign-product-to-vendors"
          title="Assign Products to Suppliers"
          icon={AssignProductsSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }

    if (
      Method.hasModulePermission(
        PermissionModule.PaymentToVendors,
        currentUser,
      ) ||
      currentUser?.roleAndPermission?.roleType === "R0001" ||
      currentUser?.roleAndPermission?.roleType === "R0003"
    ) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-payment-to-vendors"
          to="/UProcure/vendors/payments-to-vendors"
          title="Payment to Suppliers"
          icon={PaymentSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }

    // Inventory Replenishment report sits just above Purchase Orders
    if (
      currentUser?.userType == Admin ||
      Method.hasModulePermission(PermissionModule.InventoryReplenishmentReport, currentUser)
    ) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-inventory-replenishment-report"
          to="/UProcure/inventory-replenishment-report"
          title="Inventory Replenishment Report"
          icon={PurchaseOrderSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }

    if (
      Method.hasModulePermission(PermissionModule.POManagement, currentUser) ||
      currentUser?.roleAndPermission.isWarehouseManagerRole ||
      currentUser?.roleAndPermission?.roleType === "R0001" ||
      currentUser?.roleAndPermission?.roleType === "R0003"
    ) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-purchase-orders"
          to="/UProcure/purchase-order-management"
          title="Purchase Orders"
          icon={PurchaseOrderSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.GoodsReceivedNote,
        currentUser,
      ) ||
      currentUser?.roleAndPermission.isWarehouseManagerRole ||
      currentUser?.roleAndPermission?.roleType === "R0003"
    ) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-goods-received-note"
          to="/UProcure/goods-received-note"
          // Current GRN process; runs alongside Inward during the transition
          title="Goods Received Note"
          icon={GoodsReceivedNoteSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }
    if (canPerformInwardAction(currentUser, InwardAction.View)) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-inward"
          to="/UProcure/inward"
          title="Inward"
          icon={GoodsReceivedNoteSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }

    if (
      Method.hasModulePermission(PermissionModule.Escalations, currentUser) ||
      currentUser?.roleAndPermission?.roleType === "R0001" ||
      currentUser?.roleAndPermission?.roleType === "R0003"
    ) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-escalations"
          to="/UProcure/escalations"
          title="Escalations"
          icon={EscalationsSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }

    if (
      Method.hasModulePermission(PermissionModule.VendorLedger, currentUser)
    ) {
      uProcureItems.push(
        <SidebarMenuItem
          key="uprocure-vendor-ledger"
          to="/UProcure/vendor-ledger"
          title="Supplier Ledger"
          icon={SupplierLedgerSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }

    if (!showNewProcurementMenu && uProcureItems.length > 0) {
      menuItems.push(
        <SidebarMenuItemWithSub
          key="uprocure"
          to="#"
          icon={MasterSVG}
          title="Procurement"
          fontIcon="bi-app-indicator"
          activeMatch="/UProcure"
          className="release-highlighter--sidebar-menu-Procurement"
        >
          {uProcureItems.map((item: any, index: number) => {
            return <React.Fragment key={index}>{item}</React.Fragment>;
          })}
        </SidebarMenuItemWithSub>,
      );
    }

    if (showNewProcurementMenu) {
      menuItems.push(
        <SidebarMenuItem
          key="procurement-new"
          to="/procurement/master/dashboard"
          activeMatch="/procurement/"
          title="Procurement"
          icon={DetailsSVG}
          fontIcon="bi-app-indicator"
          className="release-highlighter--sidebar-menu-procurement-new"
        />,
      );
    }

    if (
      Method.hasModulePermission(PermissionModule.PriceCatalog, currentUser)
    ) {
      menuItems.push(
        <SidebarMenuItem
          key="price-catalog"
          to="/price-catalog"
          icon={CatalogueSVG}
          title="Price Catalog"
          fontIcon="bi-app-indicator"
          className="release-highlighter--sidebar-menu-price-catalog"
        />,
      );
    }

    if (
      Method.hasModulePermission(PermissionModule.Reports, currentUser) ||
      Method.hasModulePermission(
        PermissionModule.CustomerPurchaseBehaviourReport,
        currentUser,
      ) ||
      Method.hasModulePermission(
        PermissionModule.GeographicSalesInsightReport,
        currentUser,
      ) ||
      Method.hasModulePermission(
        PermissionModule.AreaBasedCustomerSegmentationReport,
        currentUser,
      ) ||
      Method.hasModulePermission(
        PermissionModule.FrequentCustomerPurchasePatternAnalysisReport,
        currentUser,
      ) ||
      Method.hasModulePermission(
        PermissionModule.ProductSalesPerformanceReport,
        currentUser,
      ) ||
      Method.hasModulePermission(
        PermissionModule.BrandWiseSalesPerformanceReport,
        currentUser,
      ) ||
      Method.hasModulePermission(
        PermissionModule.CategoryAndSubCategoryAnalysisReport,
        currentUser,
      ) ||
      Method.hasModulePermission(
        PermissionModule.RevenueGenerationReport,
        currentUser,
      ) ||
      Method.hasModulePermission(
        PermissionModule.CommissionAnalysisReport,
        currentUser,
      ) ||
      Method.hasModulePermission(
        PermissionModule.InventoryStatusReport,
        currentUser,
      )
    ) {
      menuItems.push(
        <SidebarMenuItem
          key="reports"
          to="/reports/index"
          title="Reports"
          icon={MasterSVG}
          fontIcon="bi-person"
        />,
      );
    }

    // Customers
    const customerSubItems: any = [];
    customerSubItems.push(
      <SidebarMenuItem
        key="customer-category"
        to="/customer-category"
        icon={CustomerCategorySVG}
        title={"Category"}
        fontIcon="bi-app-indicator"
      />,
    );
    if (
      Method.hasModulePermission(PermissionModule.AllCustomers, currentUser)
    ) {
      customerSubItems.push(
        <SidebarMenuItem
          key="all-customers"
          icon={CustomerSVG}
          to="/customers"
          fontIcon="bi-app-indicator"
          title="All Customers"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.RefundsToCustomer,
        currentUser,
      )
    ) {
      customerSubItems.push(
        <SidebarMenuItem
          key="refunds-to-customers"
          to="/customers/refunds-to-customers"
          title="Refunds to Customers"
          icon={RefundToCustomerSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.ReceivableReports,
        currentUser,
      )
    ) {
      customerSubItems.push(
        <SidebarMenuItem
          key="receivable-report"
          to="/customers/receivable-report"
          title="Receivable Report"
          icon={ReceivableReportSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }
    if (
      Method.hasModulePermission(PermissionModule.CustomerLedger, currentUser)
    ) {
      customerSubItems.push(
        <SidebarMenuItem
          key="customer-ledger"
          to="/customers/customer-ledger"
          title="Customer Ledger"
          icon={CustomerLedgerSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }
    if (
      Method.hasModulePermission(PermissionModule.DriverLedger, currentUser)
    ) {
      customerSubItems.push(
        <SidebarMenuItem
          key="driver-ledger"
          to="/customers/driver-ledger"
          title="Driver Ledger"
          icon={DriverLedgerSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.PreOrderCustomerList,
        currentUser,
      )
    ) {
      customerSubItems.push(
        <SidebarMenuItem
          key="pre-order-customers"
          to="/customers/pre-order-customers-list"
          title="Pre order customer"
          icon={PreOrderCustomerSVG}
          fontIcon="bi-app-indicator"
        />,
      );
    }
    if (customerSubItems?.length > 0) {
      menuItems.push(
        <SidebarMenuItemWithSub
          key="customers"
          to="/customers"
          title="Customers"
          icon={UsersIcon}
          fontIcon="bi-person"
          className="release-highlighter--sidebar-menu-Customers"
        >
          {customerSubItems}
        </SidebarMenuItemWithSub>,
      );
    }

    // Finance Reports
    const financeSubItems: any = [];

    if (
      currentUser?.userType === Admin ||
      Method.hasModulePermission(
        PermissionModule.FinanceSalesReportPermission,
        currentUser,
      )
    ) {
      financeSubItems.push(
        <SidebarMenuItem
          key="finance-sales-report"
          to="/finance-reports/finance-sales-report"
          title="Sales Reports"
        />,
      );
    }
    if (
      currentUser?.userType === Admin ||
      Method.hasModulePermission(
        PermissionModule.FinanceOrderReportPermission,
        currentUser,
      )
    ) {
      financeSubItems.push(
        <SidebarMenuItem
          key="finance-order-report"
          to="/finance-reports/finance-order-report"
          title="Order Report"
        />,
      );
    }
    if (
      currentUser?.userType == Admin ||
      Method.hasModulePermission(
        PermissionModule.OutWardReports,
        currentUser)
    ) {
      financeSubItems.push(
        <SidebarMenuItem
          key="finance-outward-report"
          to="/finance-reports/finance-outward-report"
          title="Outward Report"
          hasBullet={false}
        />
      );
    }
    if (
      currentUser?.userType == Admin ||
      Method.hasModulePermission(
        PermissionModule.FinanceDeliveryReportPermission,
        currentUser,
      )
    ) {
      financeSubItems.push(
        <SidebarMenuItem
          key="finance-delivery-report"
          to="/finance-reports/finance-delivery-report"
          title="Delivery Reports"
        />,
      );
    }
    if (
      currentUser?.userType == Admin ||
      Method.hasModulePermission(
        PermissionModule.FinanceReturnReportPermission,
        currentUser,
      )
    ) {
      financeSubItems.push(
        <SidebarMenuItem
          key="finance-return-report"
          to="/finance-reports/finance-return-report"
          title="Return Reports"
        />,
      );
    }
    if (
      currentUser?.userType == Admin ||
      Method.hasModulePermission(PermissionModule.DispatchReport, currentUser)
    ) {
      financeSubItems.push(
        <SidebarMenuItem
          key="v9-outward-report"
          to="/finance-reports/v9-outward-report"
          title="Dispatch Report"
        />,
      );
    }
    if (
      currentUser?.userType == Admin ||
      Method.hasModulePermission(
        PermissionModule.DispatchReportOnlyReturnConsidered,
        currentUser,
      )
    ) {
      financeSubItems.push(
        <SidebarMenuItem
          key="v10-outward-report"
          to="/finance-reports/v10-outward-report"
          title="Dispatch Report (Only Return Considered)"
        />,
      );
    }
    if (
      currentUser?.userType == Admin ||
      Method.hasModulePermission(
        PermissionModule.DispatchReportWithReturnedCombined,
        currentUser,
      )
    ) {
      financeSubItems.push(
        <SidebarMenuItem
          key="v8-outward-report"
          to="/finance-reports/v8-outward-report"
          title="Dispatch Report With Return Combined"
        />,
      );
    }
    if (
      currentUser?.userType == Admin ||
      Method.hasModulePermission(
        PermissionModule.ReturnProductReports,
        currentUser,
      )
    ) {
      financeSubItems.push(
        <SidebarMenuItem
          key="return-product-report"
          to="/finance-reports/return-product-report"
          title="Return Product Reports"
        />,
      );
    }
    if (
      currentUser?.userType == Admin ||
      Method.hasModulePermission(PermissionModule.DeliveryReports, currentUser)
    ) {
    }
    financeSubItems.push(
      <React.Fragment key="delivery-plan-reports">
        <SidebarMenuItem
          key="route-products-report"
          to="/finance-reports/route-products-report"
          title="Delivery Plan Report"
        />
        <SidebarMenuItem
          key="driver-reconciliation-report"
          to="/finance-reports/driver-reconciliation-report"
          title="Driver Reconciliation Report"
        />
      </React.Fragment>,
    );
    if (
      Method.hasModulePermission(
        PermissionModule.FinanceReports,
        currentUser,
      ) &&
      financeSubItems.length > 0
    ) {
      menuItems.push(
        <SidebarMenuItemWithSub
          key="finance-reports"
          to="/finance-reports"
          title="Finance Reports"
          icon={MasterSVG}
          fontIcon="bi-person"
        >
          {financeSubItems}
        </SidebarMenuItemWithSub>,
      );
    }
    // Orders & Delivery
    if (
      Method.hasModulePermission(PermissionModule.OrdersDelivery, currentUser)
    ) {
      menuItems.push(
        <SidebarMenuItem
          key="orders"
          to="/orders"
          activeMatch="/orders-delivery"
          title="Orders & Delivery"
          icon={OrdersDeliverySVG}
          fontIcon="bi-person"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.OutstandingReport,
        currentUser,
      )
    ) {
      menuItems.push(
        <SidebarMenuItem
          key="outstanding-report"
          to="/outstanding-report"
          title="Outstanding Report"
          icon={OrdersDeliverySVG}
          fontIcon="bi-person"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.PendingDeliveryOrder,
        currentUser,
      )
    ) {
      menuItems.push(
        <SidebarMenuItem
          key="pendingdeliveryorders"
          to="/pending-delivery-orders"
          title="Pending Delivery Orders"
          icon={PendingOrder}
          fontIcon="bi-person"
        />,
      );
    }

    // Master
    const masterSubItems = [];
    if (
      Method.hasModulePermission(PermissionModule.BusinessType, currentUser)
    ) {
      masterSubItems.push(
        <SidebarMenuItem
          key="business-types"
          to="/master-old/business-types"
          title="Business types"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.BusinessCertificates,
        currentUser,
      )
    ) {
      masterSubItems.push(
        <SidebarMenuItem
          key="business-certificates"
          to="/master-old/business-certificates"
          title="Business certificates"
        />,
      );
    }

    if (Method.hasModulePermission(PermissionModule.Driver, currentUser)) {
      masterSubItems.push(
        <SidebarMenuItem
          key="drivers"
          to="/master-old/drivers"
          title="Drivers"
          icon={DriverSVG}
          fontIcon="bi-person"
        />,
      );
    }
    if (Method.hasModulePermission(PermissionModule.Vehicle, currentUser)) {
      masterSubItems.push(
        <SidebarMenuItem
          key="vehicles"
          to="/master-old/vehicles"
          icon={VehicleSVG}
          fontIcon="bi-person"
          title="Vehicles"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.VehicleLoadingArea,
        currentUser,
      )
    ) {
      masterSubItems.push(
        <SidebarMenuItem
          key="loading-areas"
          to="/master-old/loading-areas"
          title="Vehicle loading areas"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.RolesAndPermission,
        currentUser,
      )
    ) {
      masterSubItems.push(
        <SidebarMenuItem
          key="roles-permissions"
          to="/master-old/roles-permissions"
          title="Roles & Permission"
        />,
      );
    }
    if (
      Method.hasModulePermission(PermissionModule.UserManagement, currentUser)
    ) {
      masterSubItems.push(
        <SidebarMenuItem
          key="User-management"
          to="/master-old/user-management"
          title="User Management"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.DistrictAndCity,
        currentUser,
      ) &&
      (currentUser?.warehouse?.defaultWarehouse ||
        currentUser?.userType == Admin ||
        currentUser?.userType == AssistAdmin)
    ) {
      masterSubItems.push(
        <SidebarMenuItem
          key="district-city"
          to="/master-old/district-city"
          icon={DistrictSVG}
          fontIcon="bi-person"
          title="City"
        />,
      );
    }

    if (!showNewMasterMenu && masterSubItems.length > 0) {
      menuItems.push(
        <SidebarMenuItemWithSub
          key="master"
          to="/master-old/business-types"
          title="Master"
          icon={MasterSVG}
          fontIcon="bi-person"
        >
          {masterSubItems}
        </SidebarMenuItemWithSub>,
      );
    }

    if (showNewMasterMenu) {
      menuItems.push(
        <SidebarMenuItem
          key="master-new"
          to="/master/roles-permissions"
          activePrefix="/master/"
          title="Master"
          icon={EscalationsSVG}
          fontIcon="bi-person"
          className="release-highlighter--sidebar-menu-master-new"
        />,
      );
    }

    // Inventory
    const inventorySubItems: any = [];
    if (
      Method.hasModulePermission(PermissionModule.GoodsWarehouse, currentUser)
    ) {
      inventorySubItems.push(
        <SidebarMenuItem
          key="goods-in-warehouse"
          to="/inventory/goods-in-warehouse"
          title="Goods in Warehouse"
          icon={GoodsInWarehouseSVG}
          fontIcon="bi-person"
        />,
      );
    }
    if (
      Method.hasModulePermission(PermissionModule.OutOfStockList, currentUser)
    ) {
      inventorySubItems.push(
        <SidebarMenuItem
          key="out-of-stock"
          to="/inventory/out-of-stock"
          title="Out of Stock List"
          icon={OutOfStockListSVG}
          fontIcon="bi-person"
        />,
      );
    }
    if (
      Method.hasModulePermission(PermissionModule.LowStockLists, currentUser)
    ) {
      inventorySubItems.push(
        <SidebarMenuItem
          key="low-stock-list"
          to="/inventory/low-stock-list"
          title="Low Stock List"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.ReceivedDirectGood,
        currentUser,
      )
    ) {
      inventorySubItems.push(
        <SidebarMenuItem
          key="received-direct-goods"
          to="/inventory/received-direct-goods"
          title="Received Direct Goods"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.ProductReturnRequests,
        currentUser,
      )
    ) {
      inventorySubItems.push(
        <SidebarMenuItem
          key="return-requests"
          to="/inventory/return-requests"
          title="Return Requests"
        />,
      );
    }
    // Inventory report
    if (
      currentUser?.userType === Admin ||
      Method.hasModulePermission(
        PermissionModule.InventoryReportPermission,
        currentUser,
      )
    ) {
      inventorySubItems.push(
        <SidebarMenuItem
          key="inventory-report"
          to="/inventory/inventory-report"
          title="Inventory Report"
        />,
      );
    }
    if (inventorySubItems.length > 0) {
      menuItems.push(
        <SidebarMenuItemWithSub
          key="inventory"
          to="/inventory/"
          title="Inventory"
          icon={InventorySVG}
          fontIcon="bi-person"
        >
          {inventorySubItems}
        </SidebarMenuItemWithSub>,
      );
    }

    const warehouseSubItems: any = [];
    if (
      currentUser?.userType == Admin ||
      (currentUser?.warehouse?.defaultWarehouse &&
        Method.hasModulePermission(
          PermissionModule.WarehouseModule,
          currentUser,
        ))
    ) {
      warehouseSubItems.push(
        <SidebarMenuItem
          key="warehouse-list"
          to="warehouse/all-warehouse"
          title="Warehouse"
          icon={WarehouseSVG}
          fontIcon="bi-person"
        />,
      );
    }
    if (
      Method.hasModulePermission(
        PermissionModule.WarehouseInterTransfer,
        currentUser,
      )
    ) {
      warehouseSubItems.push(
        <SidebarMenuItem
          key="warehouse-inter-transfer"
          to="warehouse/all-inter-transfer-requests"
          title="Inter transfer request"
          icon={InterTransferRequestSVG}
          fontIcon="bi-person"
        />,
      );
    }
    if (warehouseSubItems.length > 0) {
      menuItems.push(
        <SidebarMenuItemWithSub
          key="warehouse"
          to="/warehouse/"
          title="Warehouse"
          icon={WarehouseSVG}
          fontIcon="bi-person"
        >
          {warehouseSubItems}
        </SidebarMenuItemWithSub>,
      );
    }

    if (
      Method.hasModulePermission(
        PermissionModule.InterTransferUmart,
        currentUser,
      )
    ) {
      menuItems.push(
        <SidebarMenuItem
          key="inter-transfer-request"
          to="/inter-transfer-request"
          icon={InterTransferRequestUmartSVG}
          title={"Inter transfer request (UMart)"}
          fontIcon="bi-app-indicator"
        />,
      );
    }

    if (currentUser?.userType === Admin) {
      menuItems.push(
        <SidebarMenuItem
          key="logs"
          to="/logs/index"
          title="Audit Logs"
          icon={MasterSVG}
          fontIcon="bi-person"
        />,
      );
    }
    // All Products
    if (
      Method.hasModulePermission(PermissionModule.All_Products, currentUser) &&
      (currentUser?.warehouse?.defaultWarehouse ||
        currentUser?.userType == Admin ||
        currentUser?.userType == AssistAdmin)
    ) {
      menuItems.push(
        <SidebarMenuItem
          key="all-products"
          to="/all-products"
          title="All products"
          icon={ProductSVG}
          fontIcon="bi-person"
        />,
      );
    }
    if (
      currentUser?.userType == Admin ||
      Method.hasModulePermission(
        PermissionModule.PriceUpdateRequest,
        currentUser,
      )
    ) {
      menuItems.push(
        <SidebarMenuItem
          key="price-update-request"
          to="/price-update-request"
          title="Price Update Request"
          icon={ProductSVG}
          fontIcon="bi-person"
        />,
      );
    }
    // Platform Fees
    if (
      Method.hasModulePermission(PermissionModule.PlatformFees, currentUser)
    ) {
      menuItems.push(
        <SidebarMenuItem
          key="platform-fees"
          to="/platform-fees/fees"
          icon={PlatformSVG}
          title="Platform fees"
          fontIcon="bi-app-indicator"
        />,
      );
    }
    // Advertising
    if (Method.hasModulePermission(PermissionModule.Advertising, currentUser)) {
      menuItems.push(
        <SidebarMenuItem
          key="advertising"
          to="/advertisement/view-advertisement"
          title="Advertising"
          icon={AdvertisementSVG}
          fontIcon="bi-person"
        />,
      );
    }
    // Contact Inquiries
    if (
      Method.hasModulePermission(PermissionModule.ContactInquires, currentUser)
    ) {
      menuItems.push(
        <SidebarMenuItem
          key="contact-inquiries"
          to="/contact-inquiries/inquiries"
          icon={ContactSVG}
          title="Contact inquiries"
          fontIcon="bi-app-indicator"
        />,
      );
    }
    // Custom Notification
    if (
      Method.hasModulePermission(
        PermissionModule.CustomNotifications,
        currentUser,
      )
    ) {
      menuItems.push(
        <SidebarMenuItem
          key="custom-notification"
          to="/custom-notification"
          title="Custom Notification"
          icon={AdvertisementSVG}
          fontIcon="bi-person"
        />,
      );
    }
    if (Method.hasModulePermission(PermissionModule.Settings, currentUser)) {
      menuItems.push(
        <SidebarMenuItem
          key="setting-page"
          to="/setting/page"
          title="Settings"
          icon={SettingsSVG}
          fontIcon="bi-person"
        />,
      );
    }

    return menuItems;
  };

  return (
    <>
      {currentUser?.userType === AssistAdmin
        ? renderAssistAdminItems()
        : renderMenuItems()}
    </>
  );
};
export { SidebarMenuMain };
